<template>
  <div class="leg bg-light-subtle rounded">
    <NuxtLink class="time origin" to="/blog/time-histograms" target="_blank" style="cursor: help">
      <time-histogram
        :time="leg.departure ? dayjs(leg.departure).tz('Europe/Berlin') : undefined"
        :plannedTime="
          leg.plannedDeparture ? dayjs(leg.plannedDeparture).tz('Europe/Berlin') : undefined
        "
        :prediction="leg.departureDelayPrediction"
        :cancelled="leg.stopovers[0].cancelled"
      >
      </time-histogram>
    </NuxtLink>

    <div class="station_delay_line_container">
      <i class="icon icon-record-fill station-marker fs-5" :style="originStyle" style="top: 0"></i>
      <div v-bind:style="gradientLine" class="station_delay_line"></div>
      <i
        class="icon icon-record-fill station-marker fs-5"
        :style="destinationStyle"
        style="bottom: 0"
      ></i>
    </div>

    <div
      class="station origin"
      :class="{ 'text-decoration-line-through': leg.stopovers[0].cancelled }"
    >
      {{ leg.origin.name }}
    </div>

    <div class="platform origin">
      <outdated-info
        v-if="leg.departurePlatform"
        :real="'Gl. ' + leg.departurePlatform"
        :planned="leg.plannedDeparturePlatform ? 'Gl. ' + leg.plannedDeparturePlatform : undefined"
      ></outdated-info>
    </div>

    <div class="duration d-flex flex-column">
      <div v-if="leg.cancelled === true" class="bg-danger rounded px-2 py-1 fw-bold align-self-end">
        Entfällt
      </div>
      <time-duration
        v-else
        :duration="durations.duration"
        :planned-duration="durations.plannedDuration"
      >
      </time-duration>
    </div>

    <div class="train">
      <div class="name px-2 py-1 bg-dark rounded fit-content fw-bold">
        <i
          class="icon icon-high-speed-train"
          v-if="
            ['ICE', 'IC', 'EC', 'ECE', 'TGV', 'RJ', 'RJX', 'EN', 'FLX', 'NJ'].includes(
              leg.line.productName
            )
          "
        ></i>
        <i
          class="icon icon-bus"
          v-else-if="
            leg.line.productName === 'BUS' ||
            leg.line.productName === 'Bus' ||
            leg.line.productName === 'SEV'
          "
        ></i>
        <i class="icon icon-local-train" v-else></i>
        <span class="ms-2">{{ leg.line.name }}</span>
      </div>
      <div class="direction mt-1 fw-light">
        {{ leg.direction ? 'nach ' + leg.direction : '' }}
      </div>
    </div>

    <remark-messages style="grid-area: remarks" v-if="leg.remarks" :remarks="leg.remarks">
    </remark-messages>

    <stopover-names
      style="grid-area: times-stopovers / times-stopovers / times-stopovers / stopovers"
      v-if="leg.stopovers && leg.stopovers.length > 2"
      :stopovers="leg.stopovers"
    ></stopover-names>

    <NuxtLink
      class="time destination"
      to="/blog/time-histograms"
      target="_blank"
      style="cursor: help"
    >
      <time-histogram
        :time="leg.arrival ? dayjs(leg.arrival).tz('Europe/Berlin') : undefined"
        :plannedTime="
          leg.plannedArrival ? dayjs(leg.plannedArrival).tz('Europe/Berlin') : undefined
        "
        :prediction="leg.arrivalDelayPrediction"
        :cancelled="leg.stopovers.at(-1).cancelled"
      ></time-histogram>
    </NuxtLink>

    <div
      class="station destination"
      :class="{
        'text-decoration-line-through': leg.stopovers.at(-1).cancelled
      }"
    >
      {{ leg.destination.name }}
    </div>

    <div class="platform destination">
      <outdated-info
        v-if="leg.arrivalPlatform"
        :real="'Gl. ' + leg.arrivalPlatform"
        :planned="leg.plannedArrivalPlatform ? 'Gl. ' + leg.plannedArrivalPlatform : undefined"
      ></outdated-info>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import { rdylgrColormap, rdylgrCSSLinearGradient } from '~/utils/colormap'
import TimeDuration from '~/components/TimeDuration.vue'
import OutdatedInfo from '~/components/OutdatedInfo.vue'
import TimeHistogram from '~/components/JourneyDisplay/TimeHistogram.vue'
import { type Leg } from '~/types/fptfTypes'
import { getDurationsOfLeg, getExpectedDelay } from '~/utils/fptfHelpers'
import StopoverNames from '~/components/JourneyDisplay/StopoverNames.vue'
import RemarkMessages from '~/components/JourneyDisplay/RemarkMessages.vue'

const props = defineProps<{
  leg: Leg
}>()

const durations = computed(() => getDurationsOfLeg(props.leg))

const expectedDelayDeparture = computed(() =>
  getExpectedDelay(
    dayjs(props.leg.departure).tz('Europe/Berlin'),
    dayjs(props.leg.plannedDeparture).tz('Europe/Berlin'),
    props.leg.departureDelayPrediction
  )
)
const expectedDelayArrival = computed(() =>
  getExpectedDelay(
    dayjs(props.leg.arrival).tz('Europe/Berlin'),
    dayjs(props.leg.plannedArrival).tz('Europe/Berlin'),
    props.leg.arrivalDelayPrediction
  )
)

const gradientLine = computed(() => {
  return {
    ...(props.leg.departureDelayPrediction &&
      props.leg.arrivalDelayPrediction && {
        'background-image': rdylgrCSSLinearGradient(
          1 - expectedDelayDeparture.value / 10,
          1 - expectedDelayArrival.value / 10,
          10
        )
      })
  }
})

const originStyle = computed(() => {
  return {
    color: rdylgrColormap(1 - expectedDelayDeparture.value / 10, 0, 0.8, 200)
  }
})

const destinationStyle = computed(() => {
  return {
    color: rdylgrColormap(1 - expectedDelayArrival.value / 10, 0, 0.8, 200)
  }
})
</script>

<style lang="scss">
.fit-content {
  max-width: fit-content;
}

.leg {
  @include border-radius;
  display: inline-grid;
  padding: 15px;
  gap: 5px;
  grid-template-columns:
    $time_col_width $fancy_line_width 1fr
    max-content;
  grid-template-areas:
    'time-origin fancy-line station-origin platform-origin'
    'duration fancy-line train train'
    'empty fancy-line remarks remarks'
    'times-stopovers fancy-line stopovers stopovers'
    'time-destination fancy-line station-destination platform-destination';
}

.station {
  @include border-radius;
  font-weight: bold;
  margin: auto 0;

  overflow-wrap: anywhere;
  hyphens: auto;

  &.origin {
    grid-area: station-origin;
  }

  &.destination {
    grid-area: station-destination;
  }
}

.time {
  text-align: end;

  &.origin {
    grid-area: time-origin;
    align-self: start;
  }

  &.destination {
    grid-area: time-destination;
    align-self: end;
  }
}

.duration {
  text-align: end;
  grid-area: duration;
  align-self: center;
}

.platform {
  text-align: end;
  margin: auto 0;

  &.origin {
    grid-area: platform-origin;
    align-self: start;
  }

  &.destination {
    grid-area: platform-destination;
    align-self: end;
  }
}

.train {
  margin: 15px 0 0px 0;
  grid-area: train;
  overflow-wrap: anywhere;
  hyphens: auto;

  img {
    height: 20px;
  }
}

.station_delay_line_container {
  grid-area: fancy-line;
  display: grid;
  position: relative;
}

.station_delay_line {
  width: 2px;
  margin: 28px auto;
  z-index: 2;
}
</style>
